import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import AuthService, { AuthUser, ImpersonationState } from '../services/AuthService';

interface AuthContextType {
  currentUser: AuthUser | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  signInWithEmailAndPassword: (email: string, password: string) => Promise<void>;
  createUserWithEmailAndPassword: (email: string, password: string, displayName?: string) => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>; // New method to refresh user data
  // Impersonation methods
  isImpersonating: boolean;
  impersonatedUser: AuthUser | null;
  originalAdmin: AuthUser | null;
  startImpersonation: (userUid: string) => Promise<void>;
  stopImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Like useAuth, but undefined outside an AuthProvider (guest / embedded views, tests). */
export const useAuthSafe = (): AuthContextType | undefined => useContext(AuthContext);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<AuthUser | null>(null);
  const [originalAdmin, setOriginalAdmin] = useState<AuthUser | null>(null);

  // Initialize impersonation state from localStorage
  useEffect(() => {
    const impersonationState = AuthService.getImpersonationState();
    if (impersonationState?.isImpersonating) {
      setIsImpersonating(true);
      setImpersonatedUser(impersonationState.impersonatedUser);
      setOriginalAdmin(impersonationState.originalAdmin);
    }
  }, []);

  const signInWithGoogle = async (): Promise<void> => {
    try {
      await AuthService.signInWithGoogle();
    } catch (error: any) {
      // Handle redirect case - don't throw error as redirect is in progress
      if (error.message === 'REDIRECT_IN_PROGRESS') {
        return; // Let the redirect happen
      }
      console.error('Failed to sign in:', error);
      throw error;
    }
  };

  const signInWithMicrosoft = async (): Promise<void> => {
    try {
      await AuthService.signInWithMicrosoft();
    } catch (error: any) {
      // Handle redirect case - don't throw error as redirect is in progress
      if (error.message === 'REDIRECT_IN_PROGRESS') {
        return; // Let the redirect happen
      }
      console.error('Failed to sign in with Microsoft:', error);
      throw error;
    }
  };

  const signInWithEmailAndPassword = async (email: string, password: string): Promise<void> => {
    try {
      await AuthService.signInWithEmailAndPassword(email, password);
    } catch (error) {
      console.error('Failed to sign in with email and password:', error);
      throw error;
    }
  };

  const createUserWithEmailAndPassword = async (email: string, password: string, displayName?: string): Promise<void> => {
    try {
      await AuthService.createUserWithEmailAndPassword(email, password, displayName);
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error;
    }
  };

  const sendPasswordResetEmail = async (email: string): Promise<void> => {
    try {
      await AuthService.sendPasswordResetEmail(email);
    } catch (error) {
      console.error('Failed to send password reset email:', error);
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await AuthService.signOut();
    } catch (error) {
      console.error('Failed to sign out:', error);
      throw error;
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const updatedUser = await AuthService.refreshCurrentUser();
      setCurrentUser(updatedUser);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  };

  const startImpersonation = async (userUid: string): Promise<void> => {
    try {
      const impersonationState = await AuthService.startImpersonation(userUid);
      setIsImpersonating(true);
      setImpersonatedUser(impersonationState.impersonatedUser);
      setOriginalAdmin(impersonationState.originalAdmin);
      // currentUser is the app-wide "effective user" (org settings, currency,
      // inventory config, feature-flag org id…). onAuthStateChanged only
      // refires on a full reload, so switch it to the impersonated profile
      // now — otherwise pages like the cart render impersonated data with the
      // admin's org display config.
      setCurrentUser(impersonationState.impersonatedUser);
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      throw error;
    }
  };

  const stopImpersonation = (): void => {
    AuthService.stopImpersonation();
    setIsImpersonating(false);
    setImpersonatedUser(null);
    setOriginalAdmin(null);
    // Restore the admin's own profile: the X-Impersonate-UID header is gone
    // now, so a refetch returns the admin again (without this, currentUser
    // keeps the impersonated profile until the next full reload).
    AuthService.refreshCurrentUser()
      .then(user => { if (user) setCurrentUser(user); })
      .catch(error => console.error('Failed to restore admin profile after impersonation:', error));
  };

  useEffect(() => {
    // Check for redirect result on app initialization
    const handleRedirectResult = async () => {
      try {
        const user = await AuthService.checkRedirectResult();
        if (user) {
          console.log('Setting user from redirect result:', user);
          setCurrentUser(user);
        }
      } catch (error) {
        console.error('Error handling redirect result:', error);
      }
    };

    handleRedirectResult();

    const unsubscribe = AuthService.onAuthStateChanged((user) => {
      console.log('Auth state changed:', user);
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    loading,
    signInWithGoogle,
    signInWithMicrosoft,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    logout,
    refreshUser,
    // Impersonation
    isImpersonating,
    impersonatedUser,
    originalAdmin,
    startImpersonation,
    stopImpersonation
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
